import { useRef, useState } from "react";
import { AlertCircle, CheckCircle2, FileUp, Loader2, RotateCcw, X } from "lucide-react";

export type SpecializedUploadStatus = "idle" | "reading" | "uploading" | "extracting" | "complete";

export interface SpecializedFileDropProps {
  accept: string;
  acceptedFormats: string;
  browseLabel: string;
  dropLabel: string;
  dragActiveLabel: string;
  onFiles: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
  multiple?: boolean;
  status?: SpecializedUploadStatus;
  statusLabel?: string;
  error?: string | null;
  retryLabel?: string;
  cancelLabel?: string;
  onRetry?: () => void;
  onCancel?: () => void;
  testId?: string;
  inputTestId?: string;
  className?: string;
}

/**
 * Accessible, presentation-only file chooser. Validation and ingestion stay
 * with the specialized workflow supplied through onFiles.
 */
export function SpecializedFileDrop({
  accept,
  acceptedFormats,
  browseLabel,
  dropLabel,
  dragActiveLabel,
  onFiles,
  disabled = false,
  multiple = false,
  status = "idle",
  statusLabel,
  error,
  retryLabel,
  cancelLabel,
  onRetry,
  onCancel,
  testId = "specialized-file-drop",
  inputTestId,
  className = "",
}: SpecializedFileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragDepth, setDragDepth] = useState(0);
  const dragging = dragDepth > 0;
  const processing = status !== "idle" && status !== "complete";

  const select = async (files: FileList | File[]) => {
    const selected = Array.from(files);
    if (selected.length === 0 || disabled || processing) return;
    await onFiles(multiple ? selected : selected.slice(0, 1));
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled || processing}
        className="sr-only"
        data-testid={inputTestId ?? `${testId}-input`}
        onChange={(event) => {
          void select(event.target.files ?? []);
          event.target.value = "";
        }}
      />
      <div
        role="button"
        tabIndex={disabled || processing ? -1 : 0}
        aria-disabled={disabled || processing}
        aria-describedby={`${testId}-formats`}
        data-testid={testId}
        onKeyDown={(event) => {
          if ((event.key === "Enter" || event.key === " ") && !disabled && !processing) {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onClick={() => {
          if (!disabled && !processing) inputRef.current?.click();
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled && !processing) setDragDepth((depth) => depth + 1);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = disabled ? "none" : "copy";
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragDepth((depth) => Math.max(0, depth - 1));
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragDepth(0);
          void select(event.dataTransfer.files);
        }}
        className={`flex min-h-32 w-full flex-col items-center justify-center rounded-xl border border-dashed px-5 py-6 text-center outline-none transition-colors ${
          dragging
            ? "border-primary bg-primary/10"
            : "border-border bg-background/30 hover:border-primary/50 hover:bg-primary/5"
        } ${disabled || processing ? "cursor-not-allowed opacity-60" : "cursor-pointer focus-visible:ring-2 focus-visible:ring-primary/60"}`}
      >
        {processing ? (
          <Loader2 className="mb-2 h-6 w-6 animate-spin text-primary" aria-hidden="true" />
        ) : status === "complete" ? (
          <CheckCircle2 className="mb-2 h-6 w-6 text-emerald-400" aria-hidden="true" />
        ) : (
          <FileUp className="mb-2 h-6 w-6 text-primary" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {statusLabel ?? (dragging ? dragActiveLabel : dropLabel)}
        </span>
        {!processing && status !== "complete" && (
          <span className="mt-1 text-xs font-medium text-primary">{browseLabel}</span>
        )}
        <span id={`${testId}-formats`} className="mt-2 text-[11px] text-muted-foreground">
          {acceptedFormats}
        </span>
      </div>
      {error && (
        <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-xs text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="flex-1">{error}</span>
          {onRetry && retryLabel && (
            <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 font-semibold underline">
              <RotateCcw className="h-3 w-3" aria-hidden="true" /> {retryLabel}
            </button>
          )}
        </div>
      )}
      {processing && onCancel && cancelLabel && (
        <button
          type="button"
          onClick={onCancel}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground underline"
        >
          <X className="h-3 w-3" aria-hidden="true" /> {cancelLabel}
        </button>
      )}
    </div>
  );
}